import { useEffect, useRef, useState } from "react";
import { Copy, ImagePlus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, fmtErr } from "@/lib/api";

const MAX_BYTES = 10 * 1024 * 1024;
const TYPES = ["image/jpeg", "image/png", "image/gif", "image/webp"];

export default function AdminMediaTab() {
  const inputRef = useRef(null);
  const [assets, setAssets] = useState([]);
  const [alt, setAlt] = useState("");
  const [progress, setProgress] = useState(0);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try { const { data } = await api.get("/admin/media"); setAssets(data); }
    catch (e) { toast.error(fmtErr(e)); }
  };
  useEffect(() => { load(); }, []);

  const upload = async (file) => {
    if (!file) return;
    if (!TYPES.includes(file.type)) return toast.error("Only JPG, PNG, GIF or WebP images are allowed");
    if (file.size > MAX_BYTES) return toast.error("Images must be 10 MB or smaller");
    const body = new FormData();
    body.append("file", file);
    body.append("alt_text", alt);
    setBusy(true); setProgress(0);
    try {
      await api.post("/admin/media", body, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (e) => setProgress(Math.round((e.loaded / (e.total || file.size)) * 100)),
      });
      toast.success("Image added to the media library");
      setAlt(""); setProgress(0);
      if (inputRef.current) inputRef.current.value = "";
      load();
    } catch (e) { toast.error(fmtErr(e)); }
    finally { setBusy(false); }
  };

  const saveAlt = async (asset, value) => {
    try { await api.patch(`/admin/media/${asset.id}`, { alt_text: value }); toast.success("Alt text saved"); load(); }
    catch (e) { toast.error(fmtErr(e)); }
  };

  const remove = async (asset) => {
    if (!window.confirm(`Remove ${asset.original_filename} from the media library?`)) return;
    try { await api.delete(`/admin/media/${asset.id}`); toast.success("Image removed"); load(); }
    catch (e) { toast.error(fmtErr(e)); }
  };

  return (
    <div data-testid="admin-media-library" className="grid lg:grid-cols-[360px_1fr] gap-4">
      <section className="border border-zinc-800 bg-zinc-950 p-6 h-fit">
        <h2 className="font-display uppercase text-4xl">Upload image</h2>
        <p className="text-sm text-zinc-500 mt-2">Admin-only library for website photography. JPG, PNG, GIF or WebP up to 10 MB.</p>
        <input data-testid="media-alt-input" value={alt} onChange={(e) => setAlt(e.target.value)} placeholder="Alt text / description"
          className="mt-6 w-full bg-ink border border-zinc-700 px-4 py-3 text-sm focus:border-volt outline-none" />
        <input ref={inputRef} data-testid="media-file-input" type="file" accept=".jpg,.jpeg,.png,.gif,.webp" onChange={(e) => upload(e.target.files?.[0])} className="hidden" />
        <button data-testid="media-upload-button" onClick={() => inputRef.current?.click()} disabled={busy}
          className="mt-4 w-full inline-flex items-center justify-center gap-2 bg-volt text-black uppercase tracking-[0.16em] text-xs font-extrabold py-4 hover:bg-white disabled:opacity-50 transition-colors">
          <ImagePlus size={16} /> {busy ? `Uploading ${progress}%` : "Choose image"}
        </button>
        {busy && <div className="mt-3 h-1 bg-zinc-800"><div className="h-full bg-volt transition-all" style={{ width: `${progress}%` }} /></div>}
      </section>

      <section className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4">
        {assets.map((asset) => {
          const url = `${process.env.REACT_APP_BACKEND_URL}${asset.url}`;
          return (
            <article key={asset.id} data-testid={`media-asset-${asset.id}`} className="border border-zinc-800 bg-zinc-950 overflow-hidden">
              <img src={url} alt={asset.alt_text || asset.original_filename} className="w-full aspect-[4/3] object-cover bg-ink" />
              <div className="p-4">
                <p className="text-sm font-bold truncate">{asset.original_filename}</p>
                <p className="text-[10px] uppercase tracking-[0.14em] text-zinc-500 mt-1">{(asset.size_bytes / 1024 / 1024).toFixed(2)} MB · {asset.content_type}</p>
                <input data-testid={`media-alt-${asset.id}`} defaultValue={asset.alt_text || ""} onBlur={(e) => saveAlt(asset, e.target.value)}
                  className="mt-3 w-full bg-ink border border-zinc-800 px-3 py-2 text-xs focus:border-volt outline-none" />
                <div className="mt-3 flex gap-2">
                  <button data-testid={`media-copy-${asset.id}`} onClick={() => { navigator.clipboard.writeText(url); toast.success("Public URL copied"); }}
                    className="flex-1 inline-flex items-center justify-center gap-2 border border-zinc-700 px-3 py-2 text-[10px] uppercase tracking-[0.12em] font-bold hover:border-volt hover:text-volt"><Copy size={13} /> URL</button>
                  <button data-testid={`media-delete-${asset.id}`} onClick={() => remove(asset)}
                    className="inline-flex items-center justify-center border border-zinc-700 px-3 py-2 text-zinc-500 hover:border-red-400 hover:text-red-400"><Trash2 size={14} /></button>
                </div>
              </div>
            </article>
          );
        })}
        {!assets.length && <p className="text-sm text-zinc-600 border border-zinc-800 bg-zinc-950 p-6">No media uploaded yet.</p>}
      </section>
    </div>
  );
}
